export const EMISSIONS_KIND_IDS = [
  'emissions',
  'emissions_location',
  'biogenic_co2',
  'biogenic_co2_location',
  'clean_power_avoided_emissions',
  'co2',
  'co2_location',
  'n2o',
  'hfcs',
  'pfcs',
  'sf6',
  'nf3',
  'ch4',
  'non_kyoto_gases',
] as const;

export type EmissionsKindId = (typeof EMISSIONS_KIND_IDS)[number];

type EmissionsKindBaseDefinition = {
  id: EmissionsKindId;
  displayName: string;
  description: string;
  unit: string;
  // sql columns representing this kind in PLR / melted evaluation
  sqlQuantityField: string;
  sqlNamespacedVariableField: string;
  sqlEmissionsFactorField: string | null;
};

type GHGEmissionsKindMetadata = {
  typeId: 'ghg';
  isBiogenic: boolean;
} & (
  | {
      isLocationBased: true;
      marketBasedEmissionsKind: EmissionsKindId;
    }
  | {
      isLocationBased: false;
    }
);

type SimpleEmissionsKind = EmissionsKindBaseDefinition & {
  typeId: 'simple';
};
type GHGEmissionsKind = GHGEmissionsKindMetadata & EmissionsKindBaseDefinition;

export type EmissionsKindDefinition = SimpleEmissionsKind | GHGEmissionsKind;

const definitions: Array<EmissionsKindDefinition> = [
  {
    typeId: 'ghg',
    id: 'emissions',
    unit: 'kgco2e',
    displayName: 'Emissions',
    description:
      'Emissions for the activity. For electricity consumption, this represents the market-based allocation method.',
    sqlQuantityField: 'quantity_kg_co2e',
    sqlNamespacedVariableField: 'emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: 'ef_kg_co2e',
    isBiogenic: false,
    isLocationBased: false,
  },
  {
    typeId: 'ghg',
    id: 'emissions_location',
    unit: 'kgco2e',
    displayName: 'Location-based electricity emissions',
    description: 'Location-based emissions for electricity consumption.',
    sqlQuantityField: 'allocation_location_quantity_kg_co2e',
    sqlNamespacedVariableField:
      'allocation_location_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: true,
    marketBasedEmissionsKind: 'emissions',
  },
  {
    typeId: 'ghg',
    id: 'biogenic_co2',
    unit: 'kgco2e',
    displayName: 'Biogenic CO₂ emissions',
    description:
      'Biogenic CO₂ emissions for the activity. For electricity consumption, this represents the market-based allocation method.',
    sqlQuantityField: 'biogenic_co2_quantity_kg_co2e',
    sqlNamespacedVariableField:
      'biogenic_co2_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: true,
    isLocationBased: false,
  },
  {
    typeId: 'ghg',
    id: 'biogenic_co2_location',
    unit: 'kgco2e',
    displayName: 'Location-based Biogenic CO₂ emissions',
    description:
      'Biogenic CO₂ emissions for the activity using the location-based allocation method.',
    sqlQuantityField: 'biogenic_co2_location_quantity_kg_co2e',
    sqlNamespacedVariableField:
      'biogenic_co2_location_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: true,
    isLocationBased: true,
    marketBasedEmissionsKind: 'biogenic_co2',
  },
  {
    typeId: 'ghg',
    id: 'co2',
    unit: 'kgco2e',
    displayName: 'Carbon dioxide (CO₂)',
    description: 'Carbon dioxide emissions from non-biogenic sources.',
    sqlQuantityField: 'co2_quantity_kg_co2e',
    sqlNamespacedVariableField: 'co2_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: false,
  },
  {
    typeId: 'ghg',
    id: 'co2_location',
    unit: 'kgco2e',
    displayName: 'Location-based Carbon dioxide (CO₂)',
    description: 'Carbon dioxide emissions from non-biogenic sources.',
    sqlQuantityField: 'co2_location_quantity_kg_co2e',
    sqlNamespacedVariableField:
      'co2_location_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: true,
    marketBasedEmissionsKind: 'co2',
  },
  {
    typeId: 'ghg',
    id: 'n2o',
    unit: 'kgco2e',
    displayName: 'Nitrous oxide (N₂O)',
    description: 'Nitrous oxide emissions converted to CO₂ equivalent.',
    sqlQuantityField: 'n2o_quantity_kg_co2e',
    sqlNamespacedVariableField: 'n2o_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: false,
  },
  {
    typeId: 'ghg',
    id: 'hfcs',
    unit: 'kgco2e',
    displayName: 'Hydrofluorocarbons (HFCs)',
    description: 'Hydrofluorocarbon emissions converted to CO₂ equivalent.',
    sqlQuantityField: 'hfcs_quantity_kg_co2e',
    sqlNamespacedVariableField: 'hfcs_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: false,
  },
  {
    typeId: 'ghg',
    id: 'pfcs',
    unit: 'kgco2e',
    displayName: 'Perfluorocarbons (PFCs)',
    description: 'Perfluorocarbon emissions converted to CO₂ equivalent.',
    sqlQuantityField: 'pfcs_quantity_kg_co2e',
    sqlNamespacedVariableField: 'pfcs_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: false,
  },
  {
    typeId: 'ghg',
    id: 'sf6',
    unit: 'kgco2e',
    displayName: 'Sulfur hexafluoride (SF₆)',
    description: 'Sulfur hexafluoride emissions converted to CO₂ equivalent.',
    sqlQuantityField: 'sf6_quantity_kg_co2e',
    sqlNamespacedVariableField: 'sf6_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: false,
  },
  {
    typeId: 'ghg',
    id: 'nf3',
    unit: 'kgco2e',
    displayName: 'Nitrogen trifluoride (NF₃)',
    description: 'Nitrogen trifluoride emissions converted to CO₂ equivalent.',
    sqlQuantityField: 'nf3_quantity_kg_co2e',
    sqlNamespacedVariableField: 'nf3_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: false,
  },
  {
    typeId: 'simple',
    id: 'clean_power_avoided_emissions',
    unit: 'kgco2e',
    displayName: 'Clean power avoided emissions',
    description: 'The emissions avoided by using clean power.',
    sqlQuantityField: 'clean_power_avoided_quantity_kg_co2e',
    sqlNamespacedVariableField:
      'clean_power_avoided_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
  },

  {
    typeId: 'ghg',
    id: 'ch4',
    unit: 'kgco2e',
    displayName: 'Methane (CH₄)',
    description: 'Methane emissions converted to CO₂ equivalent.',
    sqlQuantityField: 'ch4_quantity_kg_co2e',
    sqlNamespacedVariableField: 'ch4_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: false,
  },
  {
    typeId: 'ghg',
    id: 'non_kyoto_gases',
    unit: 'kgco2e',
    displayName: 'Non-Kyoto gases',
    description: 'Non-Kyoto gas emissions converted to CO₂ equivalent.',
    sqlQuantityField: 'non_kyoto_gases_quantity_kg_co2e',
    sqlNamespacedVariableField:
      'non_kyoto_gases_emissions_model_namespaced_variable_name',
    sqlEmissionsFactorField: null,
    isBiogenic: false,
    isLocationBased: false,
  },
];

export function getAllEmissionsKindDefinitions(): Array<EmissionsKindDefinition> {
  return definitions;
}

export function isGHGEmissionsKind(
  emissionsKind: EmissionsKindDefinition
): emissionsKind is GHGEmissionsKind {
  return 'isLocationBased' in emissionsKind;
}
